import axios from 'axios';
import BaseAPI from './BaseAPI';
import _ from 'lodash';
import {
  toTSITimespan,
  baseTsiUrl,
  createEventRequestData,
  createAggregateRequestData,
  convertTsiDataToGraphData
} from './TelemetryApi/Tsi';

import { TimeBucket } from './constants';
import { parse, getCommands, getComponents, getProperties, getTelemetry } from '../utils/dtdl';
import CoreApi from './CoreApi';
import OrganizationApi from './OrganizationApi';
import { MODEL_TYPE } from '../components/Admin/Model/dtdl/AdminModelPage';

export const createArrayRequest = (apiCall) => {
  return (cancelSignal, array) => Promise.all(_.map(array, item => apiCall(cancelSignal, item)));
};

const isType = (prop, typeName) => {
  if (Array.isArray(prop)) {
    return prop.some(t => t.toLowerCase() === typeName);
  }
  return prop.toLowerCase() === typeName;

};

const convertAssetModel = (assetModel) => {
  const modules = [];
  const formattedModel = {
    ...assetModel,
    commands: [],
    telemetry: [],
    properties: []
  };

  delete formattedModel.implements;

  assetModel.modules.forEach(module => {
    modules.push({
      ...module,
      commands: [],
      telemetry: [],
      properties: []
    });
  });

  assetModel.implements.forEach(
    schema => {
      schema.schema.contents.forEach(content => {
        const componentName = content.componentName;
        const type = content.type;
        const destination = !componentName ? formattedModel : modules.find(m => m.id === componentName);

        if (!destination) {
          return;
        }

        if (isType(type, 'telemetry')) {
          destination.telemetry.push(content);
        } else if (isType(type, 'command')) {
          destination.commands.push(content);
        } else if (isType(type, 'property')) {
          destination.properties.push(content);
        }
      });
    });


  return {
    ...formattedModel,
    modules
  };
};


const mapDtdlToAssetModel = (model, models) => {
  const parsedModel = parse(model, models);

  const telemetry = getTelemetry({ dtdlModel: parsedModel, component: '.' });
  const properties = getProperties({ dtdlModel: parsedModel, component: '.' });
  const commands = getCommands({ dtdlModel: parsedModel, component: '.' });
  const modules = getComponents({ model: parsedModel, pruneRoot: true });
  const parsedModules = _.map(modules, module => {
    const t = getTelemetry({ dtdlModel: parsedModel, component: module.name });
    const p = getProperties({ dtdlModel: parsedModel, component: module.name });
    const c = getCommands({ dtdlModel: parsedModel, component: module.name });
    return {
      id: module.name,
      displayName: module.displayName ?? modules.name,
      telemetry: _.map(t, val => ({ ...val, componentName: module.name })),
      properties: _.map(p, val => ({ ...val, componentName: module.name })),
      commands: _.map(c, val => ({ ...val, componentName: module.name }))
    };
  });
  return {
    id: model['@id'],
    displayName: model.displayName,
    comment: model.comment ?? '',
    componentName: null,
    description: model.description ?? '',
    modules: parsedModules,
    telemetry: _.map(telemetry, val => ({ ...val, componentName: null })),
    properties: _.map(properties, val => ({ ...val, componentName: null })),
    commands: _.map(commands, val => ({ ...val, componentName: null }))
  };
};

class UserAPI extends BaseAPI {
  // myOrgs = (cancelSignal, userId) => this.requestGet(`users/${userId}/myorgs`, null, cancelSignal);
  // myOrgs = (cancelSignal, userId) => this.requestGet('users/myorgs', null, cancelSignal);
  whoAmI = (cancelSignal, userId) => this.requestGet(`users/${userId}/userData`, null, cancelSignal);
  updateWhoAmI = (cancelSignal, userId, userData) => this.requestPatch(`users/${userId}/userData`, userData, cancelSignal);

  getPermission = (cancelSignal, userId, resourceId) => this.requestGet(`permissions?principalId=${userId}&resourceId=${resourceId}`, null, cancelSignal);
  getPermissionsByUser = (cancelSignal, userId) => this.requestGet(`permissions?principalId=${userId}`, null, cancelSignal);
  getPermissionsByResource = (cancelSignal, resourceId) => this.requestGet(`permissions?resourceId=${resourceId}`, null, cancelSignal);
  deletePermission = (cancelSignal, userId, resourceId) => this.requestDelete(encodeURIComponent(`permissions/${userId}/${resourceId}`), null, cancelSignal);
  createPermission = (cancelSignal, permission) => this.requestPost('permissions', permission, cancelSignal);

  getUser = (cancelSignal, userId) => this.requestGet(`users/${userId}`, cancelSignal);
  deleteUser = (cancelSignal, userId) => this.requestDelete(`users/${userId}`, null, cancelSignal);
  getUsers = cancelSignal => this.requestGet('users', null, cancelSignal);
  createUser = (cancelSignal, user) => this.requestPost('users', user, cancelSignal);

  updateClaims = (cancelSignal, userId, claim) => this.requestPut(`users/${userId}/claims`, claim, cancelSignal);

  getGroups = cancelSignal => this.requestGet('groups', null, cancelSignal);

  addPermissions = (cancelSignal, users, resourceId) => {
    return Promise.all(_.map(users, user =>
      this.createPermission(cancelSignal, { principalId: user.id, resourceId: resourceId, scopes: ['READ'] })))
      .then(result => result);
  };

  deletePermissions = (cancelSignal, users, resourceId) => {
    return Promise.all(_.map(users, user =>
      this.deletePermission(cancelSignal, user.id, encodeURIComponent(resourceId))))
      .then(result => result);
  };
}

class AlarmAPI extends BaseAPI {
  getAlarms = (cancelSignal, alarmIds) => {
    if (!alarmIds) {
      return this.requestGet('alarm/status', null, cancelSignal);
    }
    const requests = alarmIds.map(id => this.createRequest('get', 'alarm/status/entity/' + id, null, cancelSignal));
    return Promise.all(requests).then(allResults => {
      return allResults.map(result => result.data);
    }).then(result => {
      return [].concat.apply([], result);
    });

  };
  acknowledgeAlarm = (cancelSignal, alarmId) => this.requestPut(`alarm/status/acknowledge/${alarmId}`, null, cancelSignal);
  getAlarmDefinitions = (cancelSignal) => this.requestGet('alarm', null, cancelSignal);
  getAlarmDefinition = (cancelSignal, alarmId) => this.requestGet('alarm', { alarmId: alarmId }, cancelSignal);
  createAlarmDefinition = (cancelSignal, alarmDef) => this.requestPost(`alarm?alarmId=${alarmDef.id}`, alarmDef, cancelSignal);
  deleteAlarmDefinition = (cancelSignal, alarmId) => this.requestDelete('alarm', { alarmId }, cancelSignal);
  updateAlarmDefinition = (cancelSignal, alarmDef) => this.requestPut(`alarm?alarmId=${alarmDef.id}`, alarmDef, cancelSignal);
  // getAlarmDefinitionFull = (cancelSignal, alarmId) => {
  //   const alarmDefPromise = this.getAlarmDefinition(cancelSignal, alarmId);
  //   const { assetApi, modelApi } = getConfig();
  //   const mApi = new ModelAPI(modelApi.url, this.auth);
  //   const aApi = new ModelAPI(assetApi.url, this.auth);
  //   const modelsPromise = mApi.getModelOverviews(cancelSignal);
  //   return Promise.all([alarmDefPromise, modelsPromise]).then(results => {
  //     const [alarmEntity, assetModels] = results;
  //     return { alarmEntity, assetModels };
  //   });
  // }
}

class AssetsAPI extends BaseAPI {
  // TODO: Implement the query thing and use that instead when there's not a demo the next day
  getAssets = cancelSignal => this.requestGet('assets', null, cancelSignal);
  getAsset = (cancelSignal, assetId) => this.requestGet(`assets/${assetId}`, null, cancelSignal);
  getAssetsByModel = (cancelSignal, modelId) => this.requestGet(`assets/model/${encodeURIComponent(modelId)}`, null, cancelSignal);
  getProviderAuthenticationKeys = (cancelSignal, providerId) => this.requestGet(`assets/${providerId}/authentication/keys`, null, cancelSignal);
  getProviderConnectionString = (cancelSignal, providerId, type) => this.requestGet(`assets/${providerId}/authentication/connectionStrings/${type}`, null, cancelSignal);
  getAssetModel = async (cancelSignal, modelId) => convertAssetModel(await this.requestGet(`capabilitymodel?modelId=${modelId}`, null, cancelSignal));
  getAssetModels = async (cancelSignal) => _.map(await this.requestGet('capabilityModel', null, cancelSignal), convertAssetModel);
  sendCommand = async (cancelSignal, assetId, command) => {
    return this.requestPost(`assets/${assetId}/executeCommand`, command, cancelSignal);
  };
  resetAsset = (cancelSignal, assetId) => this.requestPost('/assets/reset', assetId, cancelSignal);
  resetAssetModel = (cancelSignal, modelId) => this.requestPost(`assets/model/${encodeURIComponent(modelId)}/reset`, null, cancelSignal);
  resetAll = (cancelSignal) => this.requestPost('assets/resetall', null, cancelSignal);
  getTrees = (cancelSignal) => {
    return this.requestGet('trees', null, cancelSignal)
      .then((graphInfos) => _.map(graphInfos, (graphInfo) => ({ _id: graphInfo._id, id: graphInfo._key, name: graphInfo.name })));
  };
  getTree = (cancelSignal, graphId) => this.requestGet(`trees/${graphId}`, null, cancelSignal);
  getTreeItems = async (cancelSignal, key) => this.requestGet(`trees/${key}/items`, null, cancelSignal);
  saveDashboard = async (cancelSignal, dashboard) => {
    if (dashboard._key) {
      return this.requestPut(`collections/dashboards/documents/${dashboard._key}`, dashboard, cancelSignal);
    }
    return this.requestPost('collections/dashboards/documents', [dashboard], cancelSignal).then(result => result[0]);

  };
  getDashboard = async (cancelSignal, dashboardId) => this.getCollectionDocument(cancelSignal, `dashboards/${dashboardId}`);
  deleteDashboard = async (cancelSignal, dashboard) => this.requestDelete(`collections/dashboards/documents/${dashboard._key}`, null, cancelSignal);
  getDashboards = async (cancelSignal) => this.requestGet('collections/dashboards/documents', null, cancelSignal);
  getCollectionDocument = async (cancelSignal, path) => {
    // Example path: dashboard/12356

    const [collection, id] = _.split(path, '/');
    if (collection && id) {
      return this.requestGet(`collections/${collection}/documents/${id}`, null, cancelSignal);
    }
    return Promise.reject('Invalid path');
  };
  updateProps = (cancelSignal, assetId, moduleId, props) => {
    const moduleQueryParam = moduleId ? `?moduleId=${moduleId}` : '';
    return this.requestPut(
      `asset/properties/${assetId}/desired${moduleQueryParam}`,
      props,
      cancelSignal);
  };
  createAsset = (cancelSignal, asset) => this.requestPost('/assets', asset, cancelSignal);
  deleteAsset = (cancelSignal, assetId) => this.requestDelete(`/assets/${assetId}`, null, cancelSignal);
  updateAsset = (cancelSignal, assetId, body) => this.requestPatch(`/asset/${assetId}`, body, cancelSignal);

  treeRootQuery = { aql: 'FOR v,e IN 1..1 OUTBOUND \'AssetTreesNodes/AssetTreesRoot\' GRAPH \'AssetTrees\' RETURN v' };
  getAllTrees = (cancelSignal) => this.requestPost('collections/documents/query', this.treeRootQuery, cancelSignal);
  deleteTreeRoot = async (cancelSignal, root) => this.requestDelete(`trees/${root.id}`, null, cancelSignal);
  createTreeRoot = async (cancelSignal, treeRoot) => {
    return this.requestPost('collections/AssetTreesNodes/documents', [treeRoot], cancelSignal).then(result => result[0]);
  };
  createTreeNode = async (cancelSignal, treeNode) => {
    return this.requestPost('collections/AssetTreesNodes/documents', [treeNode], cancelSignal).then(result => result[0]);
  };
  updateTreeNode = (cancelSignal, treeNode) => {
    return this.requestPut(`collections/AssetTreesNodes/documents/${treeNode._key}`, treeNode, cancelSignal);
  };

  deleteTreeNode = async (cancelSignal, key) => this.requestDelete(`collections/AssetTreesNodes/documents/${key}`, null, cancelSignal);
  createTreeEdge = async (cancelSignal, edge) => {
    return this.requestPost('trees/edges', edge, cancelSignal).then(result => result);
  };
  deleteTreeEdge = async (cancelSignal, key) => this.requestDelete(`trees/edges/${key}`, null, cancelSignal);

  migrateAssetBinding = (cancelSignal, assetIds, binding) => this.requestPost(
    'migrate',
    {
      type: 'BindingModel',
      assetIds,
      bindingModelId: binding.id
    },
    cancelSignal
  );

  migrateAssetModel = (cancelSignal, migrationData) => this.requestPost('migrate', migrationData, cancelSignal);


  validateAssetBinding = (cancelSignal, binding) => {
    const assetListPromise = this.getAssetsByModel(cancelSignal, binding.assetModelId);
    return Promise.all([assetListPromise]).then(results => {
      if (_.isEmpty(results[0])) {
        return {
          assetIds: [],
          validation: {}
        };
      }
      const assetIds = _.map(results[0], asset => asset.id);
      const bindingData = {
        type: 'BindingModel',
        assetIds,
        bindingModelId: binding.id
      };
      const validatePromise = this.requestPost('/migrate/validate', bindingData, cancelSignal);
      return Promise.all([validatePromise]).then(results => {
        return {
          assetIds,
          validation: results[0]
        };
      });
    });
  };

  getAssetsByModels = (cancelSignal, assetModelList) => {
    const promises = _.map(assetModelList, assetModelId => this.getAssetsByModel(cancelSignal, assetModelId));
    return Promise.all(promises).then(results => {
      return _.unionBy(_.flatten(results), 'id');
    });
  };

}

class TsiAPI extends BaseAPI {
  constructor(tsi, token) {
    super({ baseURL: baseTsiUrl(tsi), tsiToken: token });
  }
  getTimeSeries = (cancelSignal, data) => this.requestPost('timeseries/query?api-version=2020-07-31', data, cancelSignal);
}
class TelemetryAPI extends BaseAPI {
  constructor(baseURL, tsiId) {
    super({ baseURL });
    this.tsiId = tsiId;
  }

  telemetryForAssets = async (cancelSignal, assetIds) => {
    const requests = _.map(assetIds, assetId => this.requestGet('telemetry/' + assetId, null, cancelSignal));
    return Promise.all(requests);
  };

  telemetryForAsset = (cancelSignal, assetId) => this.requestGet(`telemetry/${assetId}`, null, cancelSignal);
  getTelemetryModels = (cancelSignal) => this.requestGet('telemetrymodel', null, cancelSignal);
  getTsiToken = (cancelSignal) => this.requestGet('telemetry/tsitoken', null, cancelSignal);
  getTelemetry = async (cancelSignal, assetSignalSet, timespan, aggregate) => {
    return this.getTsiToken(cancelSignal)
      .then(data => {
        if (data.accessToken) {
          return this._loadTelemetry(cancelSignal, assetSignalSet, timespan, aggregate, data.accessToken);
        }
        throw Error('No tsi token found.');

      });
  };

  _loadTelemetry = async (cancelSignal, assetSignalsSet, timespan, aggregate, tsiToken) => {
    if (aggregate === TimeBucket.RAW) {
      return this.getEventData(cancelSignal, assetSignalsSet, toTSITimespan(timespan), tsiToken);
    }
    return this.getAggregateData(cancelSignal, assetSignalsSet, toTSITimespan(timespan), aggregate, tsiToken);

  };


  getEventData = async (cancelSignal, assetSignalsSet, timespan, tsiToken) => {
    const tsi = new TsiAPI(this.tsiId, tsiToken);
    const requests = _.map(assetSignalsSet, assetSignals => {
      return tsi.getTimeSeries(cancelSignal, createEventRequestData(assetSignals, timespan));
    });

    return Promise.all(requests)
      .then(results => {
        const dataSets = _.flatMap(results,
          (result, index) => convertTsiDataToGraphData(result, assetSignalsSet[index]));
        _.forEach(dataSets, (dataSet) => {
          dataSet.data = _.sortBy(dataSet.data, (record) => record[0]);
        });

        return dataSets;
      });
  };

  getAggregateData = async (cancelSignal, assetSignalsSet, timespan, aggregate, tsiToken) => {
    const tsi = new TsiAPI(this.tsiId, tsiToken);
    const requests = _.map(assetSignalsSet, assetSignals => {
      return tsi.getTimeSeries(cancelSignal, createAggregateRequestData(assetSignals, timespan, aggregate));
    });

    return Promise.all(requests)
      .then(results => _.flatMap(results, (result, index) => convertTsiDataToGraphData(result, assetSignalsSet[index])));
  };
}

class ModelAPI extends BaseAPI {
  // getDtdlModel = (cancelSignal, modelId, includeDrafts=false) =
  getModelOverviews = (cancelSignal, types = [], includeDrafts = false, minorVersion = false) => this.requestGet('models', { includeDrafts: includeDrafts, minorVersion: minorVersion, modelType: types }, cancelSignal);
  getModelsFull = async (cancelSignal) => {
    const models = await this.requestGet('fullmodels', null, cancelSignal);
    const assetProviderModels = _.map(_.reject(models, (model) => model.modelType === 'component'), model => model.modelData[0]);
    const dtdlModels = _.map(models, model => model.modelData[0]);

    return _.map(assetProviderModels, model => mapDtdlToAssetModel(model, dtdlModels));
  };
  getModelsFullDtdl = (cancelSignal) => this.requestGet('fullmodels', null, cancelSignal);
  getModel = (cancelSignal, type, modelId, includeDrafts = false) => this.requestGet(`models/model/${encodeURIComponent(modelId)}`, { type: type, includeDrafts: includeDrafts }, cancelSignal);
  deleteModel = (cancelSignal, type, modelId) => this.requestDelete(`models/model/${encodeURIComponent(modelId)}`, { type: type }, cancelSignal);
  getDtdlModel = (cancelSignal, modelId, includeDrafts = false) => this.requestGet(`models/dtdl/${encodeURIComponent(modelId)}`, { includeDrafts: includeDrafts }, cancelSignal);
  getProviderTemplates = (cancelSignal, modelId) => this.requestGet(`models/templates/${encodeURIComponent(modelId)}`, null, cancelSignal);
  getModelCompatibility = async (cancelSignal, type, modelId, includeDrafts = false) => {
    const model = await this.requestGet(`models/model/${encodeURIComponent(modelId)}`, { type: type, includeDrafts: includeDrafts }, cancelSignal);
    return mapDtdlToAssetModel(model.modelData[0], model.modelData);
  };
  getModelList = (cancelSignal, modelList, includeDrafts = false) => {
    const requests = _.map(modelList, modelReq => {
      return this.getModel(cancelSignal, modelReq.type, modelReq.modelId, includeDrafts);
    });
    return Promise.all(requests)
      .then(results => _.flatMap(results, res => res));
  };

  getComponentsAndModel = (cancelSignal, modelType, modelId, draft) => {
    const modelsPromise = this.getModelOverviews(cancelSignal, ['component']);
    let docPromise;
    if (draft === true) {
      docPromise = this.getDraftModel(cancelSignal, modelId);
    } else {
      docPromise = this.getModel(cancelSignal, modelType, modelId);
    }
    return Promise.all([modelsPromise, docPromise])
      .then(results => ({ models: results[0], model: results[1] }));
  };

  deleteDraftItem = (cancelSignal, model) => this.requestDelete(`models/drafts/${encodeURIComponent(model.id)}`, null, cancelSignal);
  deleteModelItem = (cancelSignal, model) => this.requestDelete(`models/model/${encodeURIComponent(model.id)}`, { type: model.modelType }, cancelSignal);

  getDraftModel = (cancelSignal, modelId) => this.requestGet(`models/drafts/${encodeURIComponent(modelId)}`, null, cancelSignal);
  updateDraft = (cancelSignal, modelEnvelope) => this.requestPut('models/drafts', modelEnvelope, cancelSignal);
  deleteDraft = (cancelSignal, modelId) => this.requestDelete(`models/drafts/${encodeURIComponent(modelId)}`, null, cancelSignal);
  publishDraft = (cancelSignal, modelId) => this.requestPost(`models/drafts/${encodeURIComponent(modelId)}/promote`, null, cancelSignal);

  getBindingDrafts = (cancelSignal) => this.requestGet('bindings/drafts', null, cancelSignal);
  getBindingDraft = (cancelSignal, bindingModelId) => this.requestGet(`bindings/drafts/${encodeURIComponent(bindingModelId)}`, null, cancelSignal);
  createBindingDraft = (cancelSignal, bindingModel) => this.requestPost('bindings/drafts', bindingModel, cancelSignal);
  updateBindingDraft = (cancelSignal, bindingModel) => this.requestPut('bindings/drafts', bindingModel, cancelSignal);
  deleteBindingDraft = (cancelSignal, bindingModel) => this.requestDelete(`bindings/drafts/${encodeURIComponent(bindingModel.id)}`, null, cancelSignal);
  publishBindingDraft = (cancelSignal, bindingModelId) => this.requestPost(`bindings/drafts/${encodeURIComponent(bindingModelId)}/promote`, null, cancelSignal);

  getBindings = (cancelSignal, assetModelId = '', includeDrafts = false,) => this.requestGet('bindings', { includeDrafts: includeDrafts, assetModelId: assetModelId }, cancelSignal);
  getBinding = (cancelSignal, bindingDescriptionId) => {
    return this.requestGet(`bindings/full/${bindingDescriptionId}`, null, cancelSignal);
  };
  getBindingsByModel = (cancelSignal, assetModelId) => this.requestGet(`bindings/model/${encodeURIComponent(assetModelId)}`, null, cancelSignal);
  createBinding = (cancelSignal, bindingDescription) => this.requestPost('bindings', bindingDescription, cancelSignal);
  deleteBinding = (cancelSignal, bindingDescription) => this.requestDelete(`bindings/${encodeURIComponent(bindingDescription.id)}`, null, cancelSignal);

  getBindingData = (cancelSignal, bindingModelId, draft, modelDraft, modelType, modelId) => {
    const modelsPromise = this.getModelOverviews(cancelSignal, ['asset', 'provider'], true, false);
    let docPromise;
    if (bindingModelId) {
      if (draft === true) {
        docPromise = this.getBindingDraft(cancelSignal, bindingModelId);
      } else {
        docPromise = this.getBinding(cancelSignal, bindingModelId);
      }
    }
    return Promise.all([docPromise, modelsPromise])
      .then(results => {
        const bindingDescription = results[0];
        const models = results[1];
        let targetPromise;
        if (modelDraft === false) {
          targetPromise = this.getModel(cancelSignal, modelType, _.first(_.split(modelId, '.')), true);
        } else {
          targetPromise = this.getDraftModel(cancelSignal, modelId);
        }

        let sourcePromise = [];
        const sources = bindingDescription.sources;
        if (_.isEmpty(_.compact(sources))) {
          sourcePromise = [];
        } else if (_.isString(_.first(sources))) {
          const req = _.map(bindingDescription.sources, modelId => {
            const found = _.filter(models, m => m.id === modelId);
            return { modelId: found[0].id, type: found[0].modelType };
          });
          sourcePromise = this.getModelList(cancelSignal, req, true);
        } else if (_.isObject(_.first(sources))) {
          _.noop();
        } else {
          sourcePromise = [];
        }
        return Promise.all(_.compact([targetPromise, sourcePromise]))
          .then(modelResult => {
            return { bindingModel: bindingDescription, overviewModels: models, targetEnvelope: modelResult[0], sources: modelResult[1] ?? [] };
          });
      });
  };
}

class CombinedApi {

  constructor({ core, assets, models, alarm }) {
    this.core = core;
    this.assets = assets;
    this.models = models;
    this.alarm = alarm;
  }

  getAssetAndProviders = (cancelSignal, assetId) => {
    let providerMap = {};
    const assetPromise = this.core.getAsset(cancelSignal, assetId);
    return assetPromise.then(asset => {
      const modelPromise = this.models.getModel(cancelSignal, MODEL_TYPE.ASSET, asset.modelId);
      const endpoints = [];
      _.forEach(asset.contentBindings, binding => {
        endpoints.push(_.map(_.uniqBy(binding.endpoints, 'instanceId'), ep => {
          return ep.instanceId;
        }));
      });
      const uniqEndpoints = _.uniq(_.flatten(endpoints));
      const providerClientPromises = _.map(uniqEndpoints, endpoint => {
        if (_.includes(endpoint, '::')) {
          const [providerId, providerClientId] = endpoint.split('::');
          providerMap[providerClientId] = providerId;
          return this.core.getProviderClient(cancelSignal, providerId, providerClientId);
        }
        providerMap[endpoint] = null;
        return this.core.getAsset(cancelSignal, endpoint);
      });
      const providerPromises = _.map(providerMap, providerId => (providerId) ? this.core.getProvider(cancelSignal, providerId) : null);
      return Promise.all([modelPromise, ...providerClientPromises]).then(result => {
        return Promise.all(providerPromises).then(providers => {
          const clients = _.slice(result, 1);
          return {
            providerClients: _.map(clients, client => ({ ...client, provider: providerMap[client.id] })),
            providers: providers,
            asset: {
              ...asset,
              model: _.first(result)
            }
          };
        });
      });
    });
  };

  getAlarmDefinitionFull = (cancelSignal, alarmId) => {
    const alarmDefPromise = this.alarm.getAlarmDefinition(cancelSignal, alarmId);
    const modelsPromise = this.models.getModelOverviews(cancelSignal);
    return Promise.all([alarmDefPromise, modelsPromise]).then(results => {
      const [alarmEntity, assetModels] = results;
      let promise;
      if (!_.isEmpty(alarmEntity.entityIds)) {
        promise = this.assets.getAsset(cancelSignal, alarmEntity.entityIds[0]);
        return Promise.all([promise]).then(result => {
          const [asset] = result;
          const assetModelEnvelope = _.filter(assetModels, model => model.id === asset.modelId)[0];
          const modelPromise = this.models.getModel(cancelSignal, assetModelEnvelope.modelType, assetModelEnvelope.id);
          const assetPromise = this.assets.getAssetsByModel(cancelSignal, assetModelEnvelope.id);
          return Promise.all([modelPromise, assetPromise]).then(result => {
            const [assetModel, assets] = result;
            return { assetModels, alarmEntity: { ...alarmEntity, entityTypeId: assetModelEnvelope.id }, assetModel, assets };
          });
        });
      }
      const assetModelEnvelope = _.filter(assetModels, model => model.id === alarmEntity.entityTypeId)[0];
      const modelPromise = this.models.getModel(cancelSignal, assetModelEnvelope.modelType, assetModelEnvelope.id);
      const assetPromise = this.assets.getAssetsByModel(cancelSignal, assetModelEnvelope.id);
      return Promise.all([modelPromise, assetPromise]).then(result => {
        const [assetModel, assets] = result;
        return { assetModels, alarmEntity: { ...alarmEntity, entityTypeId: assetModelEnvelope.id }, assetModel, assets };
      });
    });
  };

}

class API {
  static isCancel = error => axios.isCancel(error);

  constructor(config) {
    this.config = config;
    const { assetApi, alarmApi, telemetryApi, userApi, modelApi, coreApi } = config;
    this.assets = new AssetsAPI({ baseURL: assetApi.url });
    this.alarm = new AlarmAPI({ baseURL: alarmApi.url });
    this.telemetry = new TelemetryAPI(telemetryApi.url, telemetryApi.tsiId);
    this.users = new UserAPI({ baseURL: userApi.url });
    this.models = new ModelAPI({ baseURL: modelApi.url });
    this.core = new CoreApi({ baseURL: coreApi.url });
    this.combined = new CombinedApi({ core: this.core, assets: this.assets, models: this.models, alarm: this.alarm });
    this.organization = new OrganizationApi({ baseURL: coreApi.url });
  }
}

export default API;
